import { Injectable } from '@angular/core';
import { UiService } from './ui.service';
import { environment } from 'environments/environment';
import { JwtService } from './jwt.service';
import {
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse
} from '@angular/common/http';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { from, Observable, throwError } from 'rxjs';
import { ProfileService } from './profile.service';

/**
  Service that inherits Angular http service and allows us to include custom behaviour for every request;
  for instance displaying a progress bar or spinner for long-running taks
*/
@Injectable()
export class CustomHttpService implements HttpInterceptor {
  private isRefreshing = false;

  constructor(public uiService: UiService, public jwtService: JwtService, public profileService: ProfileService) {}

  /**
   * Interceptor of HTTP requests.
   * @param request
   * @param next
   */
  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const urlSubString = request.url.substring(request.url.lastIndexOf('/jobs') + 1, request.url.lastIndexOf('/'));

    // Don't Spinner for this call
    if (urlSubString !== 'jobs/reports') {
      this.uiService.displayPreloader();
    }

    return next.handle(this.addToken(request)).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          // Requests of the app
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        return this.handleError(error, request, next);
      }),
      finalize(() => this.interceptResponse())
    );
  }

  /**
   * Called when a response finalizes
   * @param jsonResponse
   * @private
   */
  private interceptResponse(jsonResponse?: any): void {
    this.uiService.hidePreloader();
  }

  private addToken(request: HttpRequest<any>) {
    const token = this.jwtService.getApiKey();
    // eslint-disable-next-line
    return token ? request.clone({ setHeaders: { 'X-AUTH-TOKEN': token } }) : request.clone();
  }

  /**
   * Handle errors when api server throws
   * @param error
   * @param request
   * @param next
   */
  private handleError(
    error: HttpErrorResponse,
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const isApiRequest = error?.url?.includes(environment.API_URL);
    const isExpiredToken = this.jwtService.isExpired(this.jwtService.getApiKey());

    if (error.status === 401 || (error.status === 0 && isApiRequest && isExpiredToken)) {
      // 401 token invalid
      return this.handleUnauthorizedResponse(error, request, next);
    }
    if (Math.floor(error.status / 100) === 5) {
      this.handleServerErrorResponse();
    }
    return throwError(() => error);
  }

  private handleUnauthorizedResponse(
    error: HttpErrorResponse,
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const sessionToken = this.jwtService.getSessionToken();
    if (sessionToken && !this.isRefreshing) {
      // try to get a new api key
      this.isRefreshing = true;
      this.jwtService.destroyApiKey();
      return from(this.profileService.validateToken()).pipe(
        switchMap(() => {
          this.isRefreshing = false;
          return next.handle(this.addToken(request));
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
    } else {
      this.uiService.setLoginEnvironment();
      return throwError(() => error);
    }
  }

  private handleServerErrorResponse() {
    window.location.replace(`${environment.SERVICES_URL}/landings/error`);
  }
}
